/**
 * Wires a segmented digit-box code input: digit-only filtering, auto-advance,
 * backspace focus-back, distribution of multi-character input (paste or fast
 * typing) across the boxes, enabling the Continue button once every box holds
 * a digit, and mirroring the assembled code into the hidden field the form
 * submits. Shared by the 2FA and password-recovery code-verification screens.
 */

/**
 * Strips every non-digit character.
 *
 * @param {string} value - raw input value
 * @returns {string} the digits contained in value, in order
 */
export const onlyDigits = (value) => (value || '').replaceAll(/\D/g, '');

/**
 * @param {string[]} values - current box values
 * @returns {boolean} true when every box holds exactly one character
 */
export const allFilled = (values) => values.every((value) => value.length === 1);

/**
 * Index of the box to focus after digits were entered into the box at index:
 * the box after the last one filled, clamped to the last box; the same box
 * when nothing was entered.
 *
 * @param {number} index - box the input event fired on
 * @param {number} digitCount - digits distributed from that event
 * @param {number} boxCount - total number of boxes
 * @returns {number} index of the box to focus
 */
export const nextFocusIndex = (index, digitCount, boxCount) => (
  Math.min(index + digitCount, boxCount - 1)
);

/**
 * @param {object} options
 * @param {string} options.boxSelector - CSS selector matching the digit boxes
 * @param {string} options.continueButtonId - id of the Continue button to gate
 * @param {string} options.codeFieldId - id of the hidden field to mirror the code into
 */
export const initSegmentedCodeInput = ({ boxSelector, continueButtonId, codeFieldId }) => {
  const boxes = Array.from(document.querySelectorAll(boxSelector));
  if (!boxes.length) return;

  const continueButton = document.getElementById(continueButtonId);
  const codeField = document.getElementById(codeFieldId);

  const syncContinue = () => {
    const values = boxes.map((box) => box.value);
    if (codeField) codeField.value = values.join('');
    if (!continueButton) return;

    continueButton.disabled = !allFilled(values);
  };

  const handleInput = (index) => {
    const digits = onlyDigits(boxes[index].value).slice(0, boxes.length - index);
    boxes[index].value = '';
    for (const [offset, digit] of [...digits].entries()) {
      boxes[index + offset].value = digit;
    }
    boxes[nextFocusIndex(index, digits.length, boxes.length)].focus();
    syncContinue();
  };

  for (const [index, box] of boxes.entries()) {
    box.addEventListener('input', () => handleInput(index));
    box.addEventListener('keydown', (event) => {
      if (event.key !== 'Backspace' || box.value !== '' || index === 0) return;

      boxes[index - 1].focus();
    });
  }
};
